import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HoverLift, GradientShift, PulseGlow } from '../ui/motion-primitives';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
}

interface CTAButton {
  text: string;
  href: string;
  onClick?: () => void;
}

interface FinalCTAProps {
  // Content Props
  title?: string;
  description?: string;
  
  // CTA Props
  primaryCTA?: CTAButton;
  pricingLink?: CTAButton;
  
  // Testimonials Props
  testimonials?: Testimonial[];
  showTestimonials?: boolean;
  
  // Style Props
  className?: string;
  variant?: 'default' | 'compact' | 'enterprise';
  
  // Animation Props
  enableAnimations?: boolean;
  staggerDelay?: number;
  
  // Behavior Props
  onCTAClick?: () => void;
}

const FinalCTA: React.FC<FinalCTAProps> = ({
  title,
  description,
  primaryCTA,
  pricingLink,
  testimonials,
  showTestimonials = false,
  className = "",
  variant = 'default',
  enableAnimations = true,
  staggerDelay = 0.1,
  onCTAClick,
  ...props
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Default content with i18n
  const defaultTitle = title || t('finalCta.title', { ns: 'homepage' });
  const defaultDescription = description || t('finalCta.description', { ns: 'homepage' });
  
  const defaultPrimaryCTA: CTAButton = primaryCTA || {
    text: t('finalCta.button', { ns: 'homepage' }),
    href: '/signup'
  };
  
  const defaultTestimonials: Testimonial[] = testimonials || [
    { 
      quote: t('testimonials.taylorO.quote', { ns: 'homepage' }), 
      author: t('testimonials.taylorO.author', { ns: 'homepage' }), 
      role: t('testimonials.taylorO.role', { ns: 'homepage' }) 
    },
    { 
      quote: t('testimonials.alexC.quote', { ns: 'homepage' }), 
      author: t('testimonials.alexC.author', { ns: 'homepage' }), 
      role: t('testimonials.alexC.role', { ns: 'homepage' }) 
    },
    { 
      quote: t('testimonials.marcusC.quote', { ns: 'homepage' }), 
      author: t('testimonials.marcusC.author', { ns: 'homepage' }), 
      role: t('testimonials.marcusC.role', { ns: 'homepage' }) 
    }
  ];

  // Handle CTA click - use navigate for internal routes
  const handleCTAClick = () => {
    if (onCTAClick) {
      onCTAClick();
    } else if (defaultPrimaryCTA.onClick) {
      defaultPrimaryCTA.onClick();
    } else if (defaultPrimaryCTA.href.startsWith('/') && !defaultPrimaryCTA.href.startsWith('//')) {
      navigate(defaultPrimaryCTA.href);
    } else {
      window.location.href = defaultPrimaryCTA.href;
    }
  };

  // Generate schema.org structured data for testimonials
  const generateTestimonialSchema = () => {
    const activeTestimonials = testimonials || defaultTestimonials;
    const reviews = activeTestimonials.map(testimonial => ({
      "@type": "Review",
      "reviewBody": testimonial.quote,
      "author": {
        "@type": "Person",
        "name": testimonial.author,
        "jobTitle": testimonial.role
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": 5,
        "bestRating": 5
      }
    }));

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Memara AI Memory System",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 5,
        "reviewCount": activeTestimonials.length,
        "bestRating": 5
      },
      "review": reviews
    };
  };

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const testimonialVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  // Variant classes
  const variantClasses = {
    default: "space-y-8 py-16 bg-gradient-to-br from-primary/5 to-primary/10",
    compact: "space-y-6 py-12 bg-gradient-to-br from-primary/3 to-primary/8",
    enterprise: "space-y-10 py-20 bg-gradient-to-br from-primary/8 to-primary/15"
  };

  // Motion components
  const MotionSection = enableAnimations ? motion.section : 'section';
  const MotionDiv = enableAnimations ? motion.div : 'div';

  return (
    <>
      {/* Schema.org structured data for testimonials */}
      {showTestimonials && (testimonials || defaultTestimonials).length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(generateTestimonialSchema())}
          </script>
        </Helmet>
      )}

      <MotionSection
        variants={enableAnimations ? itemVariants : undefined}
        initial={enableAnimations ? "hidden" : undefined}
        animate={enableAnimations ? "visible" : undefined}
        className={`${variantClasses[variant]} ${className}`}
        {...props}
      >
      <div className="text-center space-y-8 max-w-4xl mx-auto">
        {/* Main CTA Content */}
        <motion.div
          className="space-y-6"
          initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.8, delay: 0.2 } : undefined}
        >
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            initial={enableAnimations ? { opacity: 0, scale: 0.9 } : undefined}
            animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
            transition={enableAnimations ? { duration: 0.8, delay: 0.3 } : undefined}
          >
            <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
              {defaultTitle}
            </GradientShift>
          </motion.h2>
          <motion.p
            className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-light max-w-3xl mx-auto"
            initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
            animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
            transition={enableAnimations ? { duration: 0.6, delay: 0.5 } : undefined}
          >
            {defaultDescription}
          </motion.p>
        </motion.div>

        {/* Primary CTA Button */}
        <motion.div
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.5, delay: 0.7 } : undefined}
        >
          <PulseGlow color="purple" intensity={0.5}>
            <HoverLift liftAmount={6}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  variant="sanctuary"
                  size="lg"
                  onClick={handleCTAClick}
                  className="h-16 px-10 text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-2xl hover:shadow-3xl transition-all duration-300"
                >
                  {defaultPrimaryCTA.text}
                  <Sparkles className="w-6 h-6 ml-3" />
                </Button>
              </motion.div>
            </HoverLift>
          </PulseGlow>
        </motion.div>

        {pricingLink && (
          <motion.p
            className="text-sm text-muted-foreground"
            initial={enableAnimations ? { opacity: 0, y: 10 } : undefined}
            animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
            transition={enableAnimations ? { duration: 0.5, delay: 0.85 } : undefined}
          >
            <Link
              to={pricingLink.href}
              className="underline-offset-4 hover:underline hover:text-foreground transition-colors"
            >
              {pricingLink.text}
            </Link>
          </motion.p>
        )}

        {/* Testimonials */}
        {showTestimonials && defaultTestimonials.length > 0 && (
          <motion.div
            className="grid md:grid-cols-3 gap-8 mt-16"
            initial={enableAnimations ? { opacity: 0 } : undefined}
            animate={enableAnimations ? { opacity: 1 } : undefined}
            transition={enableAnimations ? { duration: 0.6, delay: 0.9 } : undefined}
          >
            {defaultTestimonials.map((testimonial, index) => (
              <MotionDiv
                key={testimonial.author}
                variants={enableAnimations ? testimonialVariants : undefined}
                initial={enableAnimations ? "hidden" : undefined}
                animate={enableAnimations ? "visible" : undefined}
                transition={enableAnimations ? { delay: 1.0 + index * staggerDelay } : undefined}
              >
                <HoverLift liftAmount={8}>
                    <Card className="text-center p-8 h-full bg-white/80 backdrop-blur-sm border-2 border-purple-100 hover:border-purple-300 shadow-xl hover:shadow-2xl transition-all duration-500 group">
                  <CardContent className="p-0 h-full flex flex-col justify-between">
                    <motion.div
                      className="mb-6"
                      initial={{ opacity: 0.8 }}
                      whileHover={{ opacity: 1 }}
                    >
                      <p className="text-gray-700 text-lg leading-relaxed italic mb-6 group-hover:text-purple-800 transition-colors">
                        "{testimonial.quote}"
                      </p>
                    </motion.div>
                    <div className="border-t border-purple-100 pt-6">
                      <div className="font-bold text-lg text-gray-900 mb-1">{testimonial.author}</div>
                      <div className="text-sm text-purple-600 font-medium">{testimonial.role}</div>
                    </div>
                  </CardContent>
                </Card>
                </HoverLift>
              </MotionDiv>
            ))}
          </motion.div>
        )}
      </div>
    </MotionSection>
    </>
  );
};

export default FinalCTA;
export type { FinalCTAProps, Testimonial, CTAButton as FinalCTAButton };
